import * as React from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { memoed, loadable, useUnits } from '@library/react-toolkit'
import { Tag, Button, Tooltip, Dropdown, Menu } from 'antd'
import {
  getTransactionsAction,
  startCollectionAction,
  Transaction,
} from './types'
import { FormattedMessage, FormattedDate } from 'react-intl'
import { pipe } from 'fp-ts/lib/function'
import styled from 'styled-components'
import { TableHeader, FittedTable } from '../styled'
import Balance from '../Balance'
import Amount from '../Amount'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  SwapOutlined,
  CaretDownFilled,
} from '@ant-design/icons'
import { option, record, array, ord, string } from 'fp-ts'
import CashoutModal from './CashoutModal'
import {
  useOnChangeHandler,
  usePaginatedData,
  usePBLPermission,
} from '../../redux'

const ordFirst = pipe(
  string.Ord,
  ord.contramap<string, [string, string]>(v => v[1])
)

const useCashoutOptions = () => {
  const balance = useSelector(s => memoed.extract(s.balance))
  const units = useUnits()
  return pipe(
    units,
    record.map(u => u.name),
    record.filterWithIndex(unitID => !!balance.balance[unitID]),
    record.toArray,
    array.sort(ordFirst)
  )
}

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`

const Wallet = () => {
  const {
    data,
    count,
    loading: transactionsLoading,
  } = usePaginatedData(s => s.wallet.transactions, 'id')
  const { page, itemsPerPage } = useSelector(s => s.wallet.query)
  const fundsLoading = useSelector(s =>
    loadable.isLoading(s.wallet.collectFundsState.result)
  )
  const unitsLoading = useSelector(s => loadable.isLoading(s.prosperus.units))
  const balanceLoading = useSelector(s => loadable.isLoading(s.balance.v))
  const onChangeHandler = useOnChangeHandler<Transaction>(getTransactionsAction)
  const permission = usePBLPermission()
  const cashOutOptions = useCashoutOptions()
  const dispatch = useDispatch()

  return (
    <>
      <CashoutModal />
      <FittedTable<Transaction & { key: string }>
        size="middle"
        title={() => (
          <TableHeader title={<FormattedMessage id="wallet" />}>
            <HeaderContent>
              <Balance />
              <Dropdown
                disabled={
                  permission !== 'CONTRIBUTOR' ||
                  fundsLoading ||
                  balanceLoading ||
                  unitsLoading ||
                  cashOutOptions.length === 0
                }
                overlay={
                  <Menu>
                    {pipe(
                      cashOutOptions,
                      array.map(([unitID, name]) => (
                        <Menu.Item
                          key={unitID}
                          onClick={() =>
                            dispatch(startCollectionAction(unitID))
                          }
                        >
                          <FormattedMessage
                            id="collectOnly"
                            values={{ unitID: name }}
                          />
                        </Menu.Item>
                      ))
                    )}
                  </Menu>
                }
              >
                <Button
                  loading={fundsLoading || balanceLoading || unitsLoading}
                  disabled={cashOutOptions.length === 0}
                  type="primary"
                >
                  <FormattedMessage id="collectFunds" /> <CaretDownFilled />
                </Button>
              </Dropdown>
            </HeaderContent>
          </TableHeader>
        )}
        dataSource={data}
        loading={transactionsLoading}
        pagination={{ pageSize: itemsPerPage, current: page + 1, total: count }}
        columns={[
          {
            dataIndex: 'id',
            title: <FormattedMessage id="id" />,
            key: 'id',
          },
          {
            dataIndex: 'createdAt',
            title: <FormattedMessage id="date" />,
            render: (_, t) => (
              <FormattedDate
                value={t.createdAt}
                year="numeric"
                month="long"
                day="numeric"
                hour="numeric"
                minute="numeric"
              />
            ),
          },
          {
            dataIndex: 'amount',
            title: <FormattedMessage id="amount" />,
            align: 'right',
            render: (_, t) => (
              <>
                <Amount amount={t.amount} unit={t.unit} />{' '}
                {t.type === 'incoming' ? (
                  <Tooltip title={<FormattedMessage id="incoming" />}>
                    <ArrowDownOutlined />
                  </Tooltip>
                ) : t.type === 'outgoing' ? (
                  <Tooltip title={<FormattedMessage id="outgoing" />}>
                    <ArrowUpOutlined />
                  </Tooltip>
                ) : (
                  <Tooltip title={<FormattedMessage id="exchange" />}>
                    <SwapOutlined />
                  </Tooltip>
                )}
              </>
            ),
          },
          {
            dataIndex: 'rib',
            title: <FormattedMessage id="rib" />,
            key: 'rib',
          },
          {
            dataIndex: 'motive',
            title: <FormattedMessage id="motive" />,
            render: (_, t) =>
              pipe(
                t.invoice,
                option.map(i => (
                  <FormattedMessage key={i.id} id="invoicePayment" values={i} />
                )),
                option.alt(() =>
                  pipe(
                    t.cashOut,
                    option.map(({ id, reference, method }) => (
                      <FormattedMessage
                        key={id}
                        id="cashOutPayment"
                        values={{ reference, method }}
                      />
                    ))
                  )
                ),
                option.getOrElse(() => <FormattedMessage id="notApplicable" />)
              ),
          },
          {
            dataIndex: 'status',
            title: <FormattedMessage id="status" />,
            render: (_, t) => (
              <Tag
                color={
                  t.status === 'success'
                    ? 'green'
                    : t.status === 'pending'
                    ? 'grey'
                    : 'red'
                }
              >
                {t.status === 'success' ? (
                  <FormattedMessage id="success" />
                ) : t.status === 'pending' ? (
                  <FormattedMessage id="pending" />
                ) : (
                  <FormattedMessage id="failed" />
                )}
              </Tag>
            ),
          },
        ]}
        onChange={onChangeHandler}
        scroll={{ y: '' }}
      />
    </>
  )
}

export default Wallet
